import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Keyboard, ShieldAlert } from 'lucide-react';
import {
  NormRect,
  RemoteInputEvent,
  classifyKey,
  createWheelAccumulator,
  letterboxPoint,
  mouseButton,
  viewLayout,
  viewPoint,
} from '../../utils/remoteInput';

/**
 * 담당자 쪽 직원 화면 + 입력 캡처.
 *
 *  - 영상은 직원 PC의 모든 모니터가 한 프레임이다. 화면 맞춤(object-contain)·모니터 하나 확대(view)·원본 크기
 *  - 좌표는 언제나 '프레임 전체' 기준 0~1로 보낸다(레터박스·확대 보정은 remoteInput의 순수 함수가 한다)
 *  - 마우스 이동은 'mv'(유실 허용)로 25ms(폴백 60ms)마다, 클릭·휠·키·글자는 'ev'(순서 보장)로 즉시
 *  - 키보드는 숨은 입력창이 받는다. 글자는 input/compositionend(한글 조합 완료)로, 단축키·기능키는 code로
 *  - 창을 떠나면(blur) 누르고 있던 키·버튼을 전부 뗀다 — 안 그러면 직원 PC에 Ctrl이 눌린 채 남는다
 *
 * ⚠ 휠은 addEventListener(passive:false)로 직접 등록한다. React onWheel은 passive라 preventDefault가 무시된다.
 */

interface Props {
  stream: MediaStream | null;
  /** 제어가 승인됐고 뷰어가 크게 보일 때만 입력을 보낸다 */
  controlActive: boolean;
  fit: 'contain' | 'actual';
  /** 화면 맞춤에서 프레임의 이 부분(모니터 하나)만 확대해 보여 준다. 없으면 전체 */
  view?: NormRect | null;
  /** 보안 데스크톱(UAC·잠금 화면) — 직원이 직접 눌러야 한다 */
  secureDesktop: boolean;
  /** 이동 이벤트 간격(DataChannel 25ms, 소켓 폴백 60ms) */
  moveIntervalMs: number;
  onInput: (kind: 'mv' | 'ev', events: RemoteInputEvent[]) => void;
  /** 영상 위에 덮는 안내(연결 중 등) */
  overlay?: React.ReactNode;
}

const PING_INTERVAL_MS = 20000;

export const RemoteScreenViewer: React.FC<Props> = ({
  stream,
  controlActive,
  fit,
  view,
  secureDesktop,
  moveIntervalMs,
  onInput,
  overlay,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const [videoSize, setVideoSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [boxSize, setBoxSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  const [keyboardFocused, setKeyboardFocused] = useState(false);

  // 모니터 하나 확대 — 화면 맞춤일 때만(원본 크기는 스크롤로 본다)
  const zoom = fit === 'contain' && view && (view.w < 1 || view.h < 1) ? view : null;
  const zoomRef = useRef<NormRect | null>(zoom);
  zoomRef.current = zoom;
  const zoomLayout = zoom ? viewLayout(boxSize.w, boxSize.h, videoSize.w, videoSize.h, zoom) : null;

  const onInputRef = useRef(onInput);
  onInputRef.current = onInput;
  const activeRef = useRef(controlActive);
  activeRef.current = controlActive;
  const moveIntervalRef = useRef(moveIntervalMs);
  moveIntervalRef.current = moveIntervalMs;

  const heldKeys = useRef(new Set<string>());
  const heldButtons = useRef(new Set<0 | 1 | 2>());
  const lastPoint = useRef<{ x: number; y: number }>({ x: 0.5, y: 0.5 });
  const lastMoveAt = useRef(0);
  const pendingMove = useRef<{ x: number; y: number } | null>(null);
  const moveTimer = useRef<number | null>(null);
  const wheelAcc = useRef(createWheelAccumulator());

  // 스트림 연결 — 원본 크기 모드로 바뀌면 같은 요소를 쓰므로 다시 붙일 필요가 없다
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.srcObject = stream;
    if (stream) v.play().catch(() => {});
  }, [stream]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const update = () => setVideoSize({ w: v.videoWidth, h: v.videoHeight });
    v.addEventListener('loadedmetadata', update);
    v.addEventListener('resize', update);
    return () => {
      v.removeEventListener('loadedmetadata', update);
      v.removeEventListener('resize', update);
    };
  }, []);

  // 확대 배치는 상자 크기로 계산한다
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setBoxSize({ w: el.clientWidth, h: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const pointOf = useCallback((clientX: number, clientY: number, clamp: boolean) => {
    const v = videoRef.current;
    if (!v) return null;
    const z = zoomRef.current;
    const wrap = wrapRef.current;
    if (z && wrap) {
      return viewPoint(clientX, clientY, wrap.getBoundingClientRect(), v.videoWidth, v.videoHeight, z, clamp);
    }
    const r = v.getBoundingClientRect();
    return letterboxPoint(clientX, clientY, r, v.videoWidth, v.videoHeight, clamp);
  }, []);

  const send = (kind: 'mv' | 'ev', events: RemoteInputEvent[]) => {
    if (events.length > 0) onInputRef.current(kind, events);
  };

  /** 누르고 있던 키·버튼을 전부 뗀다 */
  const releaseAll = useCallback(() => {
    const events: RemoteInputEvent[] = [];
    heldButtons.current.forEach(b => events.push({ t: 'm', a: 2, b, ...lastPoint.current }));
    heldKeys.current.forEach(c => events.push({ t: 'k', c, d: false }));
    heldButtons.current.clear();
    heldKeys.current.clear();
    if (events.length > 0 && activeRef.current) send('ev', events);
  }, []);

  // 제어가 끝나면 남은 키를 정리한다(직원 PC의 Rust도 게이트를 닫을 때 같은 일을 한다 — 이중 안전)
  useEffect(() => {
    if (!controlActive) {
      heldButtons.current.clear();
      heldKeys.current.clear();
      textRef.current?.blur();
    }
  }, [controlActive]);

  // 무입력 만료(직원 PC 90초) 방지 ping — 이 화면을 실제로 보고 있을 때만
  useEffect(() => {
    if (!controlActive) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') send('ev', [{ t: 'p' }]);
    }, PING_INTERVAL_MS);
    return () => window.clearInterval(t);
  }, [controlActive]);

  // 창을 떠나면 뗀다(담당자가 Alt+Tab으로 나가면 keyup이 오지 않는다)
  useEffect(() => {
    const onBlur = () => releaseAll();
    const onVis = () => {
      if (document.visibilityState !== 'visible') releaseAll();
    };
    window.addEventListener('blur', onBlur);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('blur', onBlur);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [releaseAll]);

  // 휠 — passive:false로 직접 등록해야 페이지가 함께 스크롤되지 않는다
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (!activeRef.current) return;
      e.preventDefault();
      const p = pointOf(e.clientX, e.clientY, true);
      if (!p) return;
      const { dx, dy } = wheelAcc.current(e.deltaX, e.deltaY, e.deltaMode);
      if (dx === 0 && dy === 0) return;
      lastPoint.current = p;
      send('ev', [{ t: 'w', x: p.x, y: p.y, dy, dx }]);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [pointOf]);

  const flushMove = () => {
    moveTimer.current = null;
    const p = pendingMove.current;
    if (!p) return;
    pendingMove.current = null;
    lastMoveAt.current = performance.now();
    send('mv', [{ t: 'm', a: 0, x: p.x, y: p.y }]);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!controlActive) return;
    const p = pointOf(e.clientX, e.clientY, heldButtons.current.size > 0);
    if (!p) return;
    lastPoint.current = p;
    pendingMove.current = p;
    const wait = moveIntervalRef.current - (performance.now() - lastMoveAt.current);
    if (wait <= 0) {
      if (moveTimer.current) window.clearTimeout(moveTimer.current);
      flushMove();
    } else if (!moveTimer.current) {
      moveTimer.current = window.setTimeout(flushMove, wait);
    }
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!controlActive) return;
    const b = mouseButton(e.button);
    if (b === null) return;
    const p = pointOf(e.clientX, e.clientY, false);
    if (!p) return; // 여백 클릭은 보내지 않는다
    e.preventDefault();
    textRef.current?.focus({ preventScroll: true });
    try {
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    } catch {
      /* 무시 */
    }
    // 대기 중인 이동을 먼저 비운다 — 클릭이 이동보다 앞서 도착하지 않도록 클릭에도 좌표를 싣는다
    pendingMove.current = null;
    lastPoint.current = p;
    heldButtons.current.add(b);
    send('ev', [{ t: 'm', a: 1, b, x: p.x, y: p.y }]);
  };

  const onPointerUp = (e: React.PointerEvent) => {
    const b = mouseButton(e.button);
    if (b === null || !heldButtons.current.has(b)) return;
    heldButtons.current.delete(b);
    const p = pointOf(e.clientX, e.clientY, true) ?? lastPoint.current;
    lastPoint.current = p;
    if (controlActive) send('ev', [{ t: 'm', a: 2, b, x: p.x, y: p.y }]);
  };

  // ── 키보드 (숨은 입력창) ──

  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!controlActive) return;
    const n = e.nativeEvent;
    const kind = classifyKey({
      key: e.key,
      code: e.code,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      metaKey: e.metaKey,
      isComposing: n.isComposing,
      keyCode: n.keyCode,
    });
    if (kind !== 'vk') return; // 글자는 input/compositionend가 보낸다
    e.preventDefault();
    if (e.repeat && heldKeys.current.has(e.code)) {
      // 길게 누른 반복(화살표·Backspace) — 누름만 다시 보낸다
      send('ev', [{ t: 'k', c: e.code, d: true }]);
      return;
    }
    heldKeys.current.add(e.code);
    send('ev', [{ t: 'k', c: e.code, d: true }]);
  };

  const onKeyUp = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!heldKeys.current.has(e.code)) return;
    heldKeys.current.delete(e.code);
    if (controlActive) send('ev', [{ t: 'k', c: e.code, d: false }]);
  };

  const sendTextFromBox = (text: string) => {
    if (!text || !controlActive) return;
    send('ev', [{ t: 'x', s: text }]);
  };

  const onInputEvent = (e: React.FormEvent<HTMLTextAreaElement>) => {
    const el = e.currentTarget;
    if ((e.nativeEvent as InputEvent).isComposing) return; // 한글 조합 중 — 완료(compositionend)를 기다린다
    const text = el.value;
    el.value = '';
    sendTextFromBox(text);
  };

  const composingRef = useRef(false);
  const onCompositionEnd = (e: React.CompositionEvent<HTMLTextAreaElement>) => {
    composingRef.current = false;
    const el = e.currentTarget;
    sendTextFromBox(e.data);
    // 조합 완료 직후 값을 바로 지우면 다음 음절 조합이 첫 글자를 잃는 IME가 있다 — 한 박자 뒤에, 새 조합이 없을 때만
    window.setTimeout(() => {
      if (!composingRef.current) el.value = '';
    }, 0);
  };

  const actual = fit === 'actual' && videoSize.w > 0;

  return (
    <div
      ref={wrapRef}
      className={`relative w-full h-full bg-black select-none ${actual ? 'overflow-auto' : 'overflow-hidden'} ${
        controlActive ? 'cursor-default' : ''
      }`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onContextMenu={(e) => controlActive && e.preventDefault()}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className={actual ? 'block max-w-none' : zoomLayout ? 'absolute block max-w-none' : 'block w-full h-full object-contain'}
        style={
          actual
            ? { width: videoSize.w, height: videoSize.h }
            : zoomLayout
              ? { left: zoomLayout.left, top: zoomLayout.top, width: zoomLayout.width, height: zoomLayout.height }
              : undefined
        }
      />

      {/* 키보드를 받는 숨은 입력창 — 한글 IME가 여기서 조합한다 */}
      <textarea
        ref={textRef}
        aria-label="원격 키보드 입력"
        className="absolute left-2 top-2 w-px h-px opacity-0 resize-none pointer-events-none"
        autoComplete="off"
        autoCorrect="off"
        spellCheck={false}
        tabIndex={controlActive ? 0 : -1}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        onInput={onInputEvent}
        onCompositionStart={() => { composingRef.current = true; }}
        onCompositionEnd={onCompositionEnd}
        onFocus={() => setKeyboardFocused(true)}
        onBlur={() => {
          setKeyboardFocused(false);
          releaseAll();
        }}
      />

      {controlActive && (
        <div className="absolute left-1/2 -translate-x-1/2 bottom-3 pointer-events-none flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/70 text-white text-xs">
          <Keyboard size={13} aria-hidden="true" />
          {keyboardFocused ? '키보드 입력이 직원 PC로 갑니다' : '화면을 클릭하면 키보드 입력이 직원 PC로 갑니다'}
        </div>
      )}

      {secureDesktop && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/75 p-6 text-center pointer-events-none">
          <div className="max-w-sm text-white">
            <ShieldAlert size={36} className="mx-auto mb-3 text-orange-400" aria-hidden="true" />
            <p className="font-semibold">직원 PC에 관리자 확인 창이나 잠금 화면이 떠 있습니다</p>
            <p className="mt-1 text-sm text-slate-300">원격으로는 보이지도 눌리지도 않습니다. 직원에게 직접 눌러 달라고 요청해 주세요.</p>
          </div>
        </div>
      )}

      {overlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-slate-900 p-6 text-center">{overlay}</div>
      )}
    </div>
  );
};
